import type { ColumnDef } from '@tanstack/react-table';
import { Plus, Tag } from 'lucide-react';
import { useMemo, useState } from 'react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ConfirmDialog } from '@/shared/components/confirm-dialog';
import { DataTable } from '@/shared/components/data-table/data-table';
import { DataTablePagination } from '@/shared/components/data-table/data-table-pagination';
import { DataTableToolbar } from '@/shared/components/data-table/data-table-toolbar';
import { useDataTable } from '@/shared/components/data-table/use-data-table';
import { EmptyState } from '@/shared/components/empty-state';
import { SearchInput } from '@/shared/components/search-input';
import { StatusBadge } from '@/shared/components/status-badge';
import { useDebouncedValue } from '@/shared/hooks/use-debounced-value';
import { useTableSearchParams } from '@/shared/hooks/use-table-search-params';
import { formatDateTime } from '@/shared/lib/format';

import { useLoyaltyBrandRules, useSetLoyaltyBrandRuleActive } from '../api/loyalty.queries';
import type { LoyaltyBrandRule, LoyaltyBrandRuleStatus } from '../types/loyalty.types';
import { BrandRuleDialog } from './brand-rule-dialog';

const ANY = 'any';

type Open =
  | { kind: 'create' }
  | { kind: 'edit'; rule: LoyaltyBrandRule }
  | { kind: 'status'; rule: LoyaltyBrandRule }
  | null;

function brandLabel(rule: LoyaltyBrandRule) {
  return rule.brandName ?? `Brand #${rule.brandId}`;
}

function columns(
  canManage: boolean,
  defaultPoints: number | null,
  setOpen: (open: Open) => void,
): ColumnDef<LoyaltyBrandRule, unknown>[] {
  const base: ColumnDef<LoyaltyBrandRule, unknown>[] = [
    {
      id: 'brand',
      header: 'Brand',
      enableSorting: false,
      enableHiding: false,
      cell: ({ row }) => <span className="font-medium">{brandLabel(row.original)}</span>,
    },
    {
      id: 'points',
      header: 'Points per item',
      enableSorting: false,
      cell: ({ row }) => (
        <div>
          <span className="font-medium tabular-nums">{row.original.points}</span>
          {!row.original.isActive && (
            <span className="text-muted-foreground block text-[11px]">
              Falls back to the default
              {defaultPoints === null ? ' (disabled)' : ` (${defaultPoints})`}
            </span>
          )}
        </div>
      ),
    },
    {
      id: 'status',
      header: 'Status',
      enableSorting: false,
      cell: ({ row }) =>
        row.original.isActive ? (
          <StatusBadge variant="success">Active</StatusBadge>
        ) : (
          <StatusBadge variant="neutral">Inactive</StatusBadge>
        ),
    },
    {
      id: 'updated',
      header: 'Last changed',
      enableSorting: false,
      cell: ({ row }) => (
        <span className="text-muted-foreground">
          {formatDateTime(row.original.updatedAt)}
          {row.original.updatedByName && (
            <span className="block text-[11px]">{row.original.updatedByName}</span>
          )}
        </span>
      ),
    },
  ];
  if (!canManage) return base;
  return [
    ...base,
    {
      id: 'actions',
      header: () => <span className="sr-only">Actions</span>,
      enableSorting: false,
      enableHiding: false,
      cell: ({ row }) => (
        <div className="flex justify-end gap-1">
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setOpen({ kind: 'edit', rule: row.original })}
            aria-label={`Edit points for ${brandLabel(row.original)}`}
          >
            Edit
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setOpen({ kind: 'status', rule: row.original })}
            aria-label={`${row.original.isActive ? 'Deactivate' : 'Activate'} rule for ${brandLabel(row.original)}`}
          >
            {row.original.isActive ? 'Deactivate' : 'Activate'}
          </Button>
        </div>
      ),
    },
  ];
}

/**
 * Brand rules of the country: every denomination of a brand earns the brand's points instead of
 * the default. A deactivated rule is kept and the brand falls back to the default.
 */
export function BrandRulesSection({
  countryId,
  countryName,
  defaultPoints,
  canManage,
}: {
  countryId: number;
  countryName: string;
  defaultPoints: number | null;
  canManage: boolean;
}) {
  const tableState = useTableSearchParams();
  const search = useDebouncedValue(tableState.search, 300);
  const statusParam = tableState.getFilter('status') ?? ANY;
  const status: LoyaltyBrandRuleStatus | undefined =
    statusParam === 'active' || statusParam === 'inactive' ? statusParam : undefined;
  const [open, setOpen] = useState<Open>(null);
  const setActive = useSetLoyaltyBrandRuleActive();

  const params = useMemo(
    () => ({
      page: tableState.page,
      pageSize: tableState.pageSize,
      search: search || undefined,
      status,
    }),
    [tableState.page, tableState.pageSize, search, status],
  );
  const rules = useLoyaltyBrandRules(countryId, params);
  const tableColumns = useMemo(
    () => columns(canManage, defaultPoints, setOpen),
    [canManage, defaultPoints],
  );
  const table = useDataTable({
    data: rules.data,
    columns: tableColumns,
    tableState,
    getRowId: (r) => String(r.id),
  });

  const statusTarget = open?.kind === 'status' ? open.rule : null;
  const defaultText =
    defaultPoints === null ? ' (currently disabled, so none)' : ` (${defaultPoints})`;

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-2">
        <div>
          <CardTitle className="text-sm">Brand rules</CardTitle>
          <CardDescription className="text-xs">
            Points per purchased item for every denomination of a brand, instead of the default.
          </CardDescription>
        </div>
        {canManage && (
          <Button size="sm" variant="outline" onClick={() => setOpen({ kind: 'create' })}>
            <Plus className="size-3.5" aria-hidden />
            Brand rule
          </Button>
        )}
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        <DataTableToolbar
          hasActiveFilters={tableState.hasActiveFilters}
          onResetFilters={tableState.resetFilters}
        >
          <SearchInput
            value={tableState.search}
            onChange={tableState.setSearch}
            placeholder="Search brand…"
            className="w-full sm:w-64"
          />
          <Select
            value={statusParam}
            onValueChange={(value) => tableState.setFilter('status', value)}
          >
            <SelectTrigger size="sm" className="h-8 w-36 text-xs" aria-label="Filter by status">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ANY} className="text-xs">
                Any status
              </SelectItem>
              <SelectItem value="active" className="text-xs">
                Active
              </SelectItem>
              <SelectItem value="inactive" className="text-xs">
                Inactive
              </SelectItem>
            </SelectContent>
          </Select>
        </DataTableToolbar>
        <DataTable
          table={table}
          isLoading={rules.isLoading}
          isFetching={rules.isFetching}
          error={rules.error}
          onRetry={() => void rules.refetch()}
          emptyState={
            tableState.hasActiveFilters ? (
              <EmptyState
                icon={Tag}
                title="No brand rules match"
                description="Try another search or status."
                action={
                  <Button variant="outline" size="sm" onClick={tableState.resetFilters}>
                    Clear filters
                  </Button>
                }
              />
            ) : (
              <EmptyState
                icon={Tag}
                title="No brand rules"
                description="Every brand earns the default purchase points."
              />
            )
          }
        />
        <DataTablePagination table={table} />
      </CardContent>

      {open?.kind === 'create' && (
        <BrandRuleDialog
          countryId={countryId}
          countryName={countryName}
          defaultPoints={defaultPoints}
          onClose={() => setOpen(null)}
        />
      )}
      {open?.kind === 'edit' && (
        <BrandRuleDialog
          countryId={countryId}
          countryName={countryName}
          defaultPoints={defaultPoints}
          rule={open.rule}
          onClose={() => setOpen(null)}
        />
      )}
      <ConfirmDialog
        open={statusTarget !== null}
        onOpenChange={(isOpen) => !isOpen && setOpen(null)}
        title={statusTarget?.isActive ? 'Deactivate this rule?' : 'Activate this rule?'}
        description={
          statusTarget?.isActive
            ? `Every ${statusTarget ? brandLabel(statusTarget) : ''} denomination will earn the default purchase points${defaultText} from the next purchase. The rule is kept and can be activated again.`
            : `Every ${statusTarget ? brandLabel(statusTarget) : ''} denomination will earn ${statusTarget?.points ?? 0} points per item again from the next purchase.`
        }
        confirmLabel={statusTarget?.isActive ? 'Deactivate' : 'Activate'}
        variant={statusTarget?.isActive ? 'destructive' : 'default'}
        isPending={setActive.isPending}
        onConfirm={() =>
          statusTarget &&
          setActive.mutate(
            { id: statusTarget.id, active: !statusTarget.isActive },
            { onSuccess: () => setOpen(null) },
          )
        }
      />
    </Card>
  );
}
